import type { SiteBuilderInnerBlockType } from "./innerContentBlocks";

export const SITE_BUILDER_INNER_CONTENT_TRANSLATION_GROUP_KEYS = [
    "content",
    "media",
    "action",
    "data",
    "general",
] as const;

export type SiteBuilderInnerContentTranslationGroupKey =
    typeof SITE_BUILDER_INNER_CONTENT_TRANSLATION_GROUP_KEYS[number];

export type SiteBuilderInnerContentTranslationField = Readonly<{
    blockType?: string;
    fieldCode: string;
}>;

export type SiteBuilderInnerContentTranslationGroup<Field> = {
    fields: Field[];
    key: SiteBuilderInnerContentTranslationGroupKey;
};

type SemanticFieldRegistry = Record<
    SiteBuilderInnerBlockType,
    Partial<Record<
        Exclude<SiteBuilderInnerContentTranslationGroupKey, "general">,
        readonly string[]
    >>
>;

/**
 * Semantic sections shared by Web and Shop inner-content blocks.
 *
 * This is editor-only metadata. Translation identities and persisted field codes stay
 * unchanged, while each product remains responsible for its own translated labels.
 */
const semanticFields = {
    text: {
        content: ["text"],
    },
    card: {
        content: ["eyebrow", "title", "subtitle", "text", "description"],
        media: [
            "imageUrl",
            "mediaUrl",
            "videoUrl",
            "posterUrl",
            "alt",
            "imageTitle",
        ],
        action: [
            "actionLabel",
            "actionUrl",
            "buttonLabel",
            "buttonUrl",
            "label",
            "href",
        ],
    },
    button: {
        action: ["label", "href"],
    },
    separator: {},
    image: {
        media: ["imageUrl", "src", "alt", "title"],
    },
    video: {
        media: ["videoUrl", "src", "posterUrl", "alt", "title"],
    },
    icon: {
        content: ["label"],
        media: ["icon"],
        action: ["href"],
    },
    accordion: {
        content: ["title", "text"],
    },
    alert: {
        content: ["title", "text"],
    },
    iconList: {
        content: ["title"],
        data: ["items"],
    },
    rating: {
        content: ["title"],
        data: ["rating", "max", "ratingIcon"],
    },
    share: {
        content: ["title"],
        data: ["items", "networks"],
    },
    socialMedia: {
        content: ["title"],
        data: ["items", "networks"],
    },
    searchBar: {
        content: ["placeholder"],
        action: ["buttonLabel"],
    },
    textHighlight: {
        content: ["eyebrow", "title", "text"],
    },
    chart: {
        content: ["title"],
        data: ["chartRows", "chartSeries", "rows", "series"],
    },
    progressBar: {
        content: ["title"],
        data: ["value", "max"],
    },
    badge: {
        content: ["label"],
    },
    ctaBadge: {
        content: ["title"],
        action: ["label", "href"],
    },
    avatars: {
        content: ["title"],
        data: ["items"],
    },
    blockquote: {
        content: ["text", "author"],
        media: ["imageUrl", "alt"],
    },
    form: {
        content: ["successMessage"],
        action: ["buttonLabel", "submitLabel"],
        data: ["fields"],
    },
    countdown: {
        content: ["title"],
        data: ["date", "endDate"],
    },
    code: {
        data: ["code", "language"],
    },
    map: {
        content: ["title"],
        data: ["address", "mapUrl", "latitude", "longitude"],
    },
    donationButton: {
        action: ["label", "href"],
        data: ["icon", "amount"],
    },
    clientPortalButton: {
        action: ["label"],
        data: ["icon"],
    },
    addToCartButton: {
        action: ["label"],
        data: ["productUuid", "icon"],
    },
    supportedPaymentButton: {
        content: ["title"],
        data: ["items"],
    },
} as const satisfies SemanticFieldRegistry;

function contentFieldName(fieldCode: string): string {
    return fieldCode.startsWith("content.")
        ? fieldCode.slice("content.".length)
        : fieldCode;
}

function isInnerContentBlockType(
    value: string,
): value is SiteBuilderInnerBlockType {
    return Object.hasOwn(semanticFields, value);
}

function groupForField(
    blockType: SiteBuilderInnerBlockType,
    fieldCode: string,
): SiteBuilderInnerContentTranslationGroupKey {
    const fieldName = contentFieldName(fieldCode);
    const definitions = (semanticFields as SemanticFieldRegistry)[blockType];

    if (!definitions) {
        return "general";
    }

    for (const key of SITE_BUILDER_INNER_CONTENT_TRANSLATION_GROUP_KEYS) {
        if (key !== "general" && definitions[key]?.includes(fieldName)) {
            return key;
        }
    }

    return "general";
}

/**
 * Returns `null` for non-inner blocks so a product can retain its own semantic
 * grouping rules. Shared inner blocks always return at least one group.
 */
export function groupSiteBuilderInnerContentTranslationFields<
    Field extends SiteBuilderInnerContentTranslationField,
>(
    fields: readonly Field[],
): SiteBuilderInnerContentTranslationGroup<Field>[] | null {
    const blockType = fields.find((field) => (
        typeof field.blockType === "string"
        && isInnerContentBlockType(field.blockType)
    ))?.blockType;

    if (!blockType || !isInnerContentBlockType(blockType)) {
        return null;
    }

    const buckets = new Map<
        SiteBuilderInnerContentTranslationGroupKey,
        SiteBuilderInnerContentTranslationGroup<Field>
    >();

    for (const field of fields) {
        const key = groupForField(blockType, field.fieldCode);
        const existing = buckets.get(key);

        if (existing) {
            existing.fields.push(field);
        } else {
            buckets.set(key, {
                fields: [field],
                key,
            });
        }
    }

    return SITE_BUILDER_INNER_CONTENT_TRANSLATION_GROUP_KEYS.flatMap((key) => {
        const group = buckets.get(key);

        return group ? [group] : [];
    });
}
